/**
 * Shared building blocks for the list pages (Hardware, Users, Companies,
 * Mix Designs, Limits & Targets, ...): sticky-header table styling, clickable status summary
 * cards, and the label/value item used in expanded row panels.
 *
 * The located-entity list layout (Projects, Batching Plants) is also built
 * from here: toolbar with a status switch, a map linked to the list, name /
 * address / status cells, a row actions menu and a selectable row with a
 * detail strip.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TableCell,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloseFullscreen as CollapseMapIcon,
  MoreVert as MoreIcon,
  OpenInFull as ExpandMapIcon,
  Place as PlaceIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WrongLocation as NoLocationIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export const HEADER_CELL_SX = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
  color: 'text.secondary',
  whiteSpace: 'nowrap' as const,
  backgroundColor: 'grey.50',
};

export interface SummaryCardDef<K extends string> {
  key: K;
  label: string;
  count: number;
  icon: React.ReactElement;
  color: string;
}

interface StatusSummaryCardsProps<K extends string> {
  cards: Array<SummaryCardDef<K>>;
  selected: K;
  /** Key that represents "no filter"; clicking the selected card resets to it. */
  allKey: K;
  onSelect: (key: K) => void;
}

export function StatusSummaryCards<K extends string>({
  cards,
  selected,
  allKey,
  onSelect,
}: StatusSummaryCardsProps<K>) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${Math.min(cards.length, 2)}, 1fr)`,
          sm: `repeat(${cards.length}, minmax(0, 220px))`,
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      {cards.map((card) => {
        const isSelected = selected === card.key;
        return (
          <Card
            key={card.key || 'all'}
            variant="outlined"
            onClick={() => onSelect(isSelected && card.key !== allKey ? allKey : card.key)}
            sx={{
              cursor: 'pointer',
              borderRadius: 2,
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderColor: isSelected ? card.color : 'divider',
              borderWidth: isSelected ? 2 : 1,
              backgroundColor: isSelected ? `${card.color}0d` : 'background.paper',
              transition: 'border-color 0.15s, background-color 0.15s',
              '&:hover': { borderColor: card.color, backgroundColor: `${card.color}14` },
            }}
          >
            <Box sx={{ color: card.color, display: 'flex' }}>{card.icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {card.count}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {card.label}
              </Typography>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
}

export const DetailItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }} component="div">
      {children}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Location helpers
// ---------------------------------------------------------------------------

type Coord = string | number | null | undefined;

export const hasCoords = (lat: Coord, lng: Coord): boolean =>
  lat !== null && lat !== undefined && lat !== '' &&
  lng !== null && lng !== undefined && lng !== '' &&
  !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));

/** Split a free-text address into a street line and a locality line at the first comma. */
export const splitAddress = (address?: string | null): [string, string] | null => {
  const text = address?.trim();
  if (!text) return null;
  const idx = text.indexOf(',');
  if (idx === -1) return [text, ''];
  return [text.slice(0, idx).trim(), text.slice(idx + 1).trim()];
};

/** Google Maps directions link to an address, falling back to coordinates. */
export const directionsUrl = (address?: string | null, lat?: Coord, lng?: Coord): string | null => {
  const destination = address?.trim() || (hasCoords(lat, lng) ? `${lat},${lng}` : '');
  return destination
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
    : null;
};

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

export interface StatusOption<K extends string> {
  key: K;
  label: string;
  count?: number;
}

interface ListToolbarProps<K extends string> {
  search: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  statusOptions: Array<StatusOption<K>>;
  status: K;
  onStatus: (key: K) => void;
  onRefresh: () => void;
  loading?: boolean;
  /** Omit to hide the add button (no create permission). */
  addLabel?: string;
  onAdd?: () => void;
  /** Extra filter controls (type selects...) placed after the status switch. */
  filters?: React.ReactNode;
  /** Extra buttons placed before the refresh button. */
  extraActions?: React.ReactNode;
}

/** Search, All / Active / Inactive switch, refresh and the add button. */
export function ListToolbar<K extends string>({
  search, onSearch, searchPlaceholder, statusOptions, status, onStatus, onRefresh, loading, addLabel, onAdd,
  filters, extraActions,
}: ListToolbarProps<K>) {
  const { t } = useTranslation();
  return (
    <Box mb={2} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
      <TextField
        size="small"
        variant="outlined"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        sx={{ flex: 1, minWidth: 200, maxWidth: { xs: '100%', sm: 400 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        value={status}
        onChange={(_, v: K | null) => v && onStatus(v)}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.5 } }}
      >
        {statusOptions.map((o) => (
          <ToggleButton key={o.key} value={o.key}>
            {o.count === undefined ? o.label : `${o.label} · ${o.count}`}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {filters}
      <Box sx={{ flexGrow: 1 }} />
      {extraActions}
      <Tooltip title={t('common.refresh')}>
        <span>
          <IconButton onClick={onRefresh} disabled={loading} aria-label={t('common.refresh')}>
            <RefreshIcon />
          </IconButton>
        </span>
      </Tooltip>
      {addLabel && onAdd && (
        <Button
          variant="contained"
          size="medium"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 3, py: 1 }}
        >
          {addLabel}
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** Bold name with an optional secondary line (client, supplier...). */
export const NameCell: React.FC<{ name: React.ReactNode; subtitle?: string | null }> = ({ name, subtitle }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="body2" fontWeight={600} noWrap>
      {name}
    </Typography>
    {subtitle?.trim() && (
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {subtitle}
      </Typography>
    )}
  </Box>
);

/** Coloured dot plus label; `textColor` defaults to the dot colour. */
export const ToneDot: React.FC<{ color: string; label: React.ReactNode; textColor?: string }> = ({
  color, label, textColor,
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      fontSize: 13,
      color: textColor ?? color,
      whiteSpace: 'nowrap',
    }}
  >
    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: color }} />
    {label}
  </Box>
);

/** Active / inactive dot, used in place of a status chip. */
export const StatusDot: React.FC<{ active: boolean; activeLabel: string; inactiveLabel: string }> = ({
  active, activeLabel, inactiveLabel,
}) => (
  <ToneDot
    color={active ? 'success.main' : 'grey.400'}
    textColor={active ? 'success.dark' : 'text.secondary'}
    label={active ? activeLabel : inactiveLabel}
  />
);

/**
 * Two-line address with a pin showing whether the record is on the map.
 * With no address: an "Add address" link when onAdd is given, else a muted note.
 */
export const AddressCell: React.FC<{ address?: string | null; mapped: boolean; onAdd?: () => void }> = ({
  address, mapped, onAdd,
}) => {
  const { t } = useTranslation();
  const parts = splitAddress(address);
  if (!parts) {
    return onAdd ? (
      <Link
        component="button"
        variant="caption"
        underline="hover"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onAdd();
        }}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'warning.dark' }}
      >
        <NoLocationIcon sx={{ fontSize: 16 }} />
        {t('listPage.addAddress', 'Add address')}
      </Link>
    ) : (
      <Typography variant="caption" color="text.disabled">
        {t('listPage.noAddress', 'No address')}
      </Typography>
    );
  }
  const [street, locality] = parts;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
      <Tooltip title={mapped ? t('listPage.onMap', 'Shown on map') : t('listPage.notOnMap', 'Not on map (no coordinates)')}>
        <PlaceIcon sx={{ fontSize: 18, mt: '1px', flexShrink: 0, color: mapped ? 'success.main' : 'text.disabled' }} />
      </Tooltip>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>{street}</Typography>
        {locality && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {locality}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Row actions
// ---------------------------------------------------------------------------

export interface RowAction {
  key: string;
  label: string;
  icon: React.ReactElement;
  onClick?: () => void;
  /** Opens in a new tab instead of running onClick. */
  href?: string;
  disabled?: boolean;
  color?: 'error' | 'warning' | 'success';
}

/** Falsy entries are dropped, so callers can write `canEdit && {...}`. */
export type MaybeActions = Array<RowAction | false | null | undefined>;
const compactActions = (actions: MaybeActions) => actions.filter(Boolean) as RowAction[];

/** The ⋮ button and its menu. */
export const RowActionsMenu: React.FC<{ actions: MaybeActions }> = ({ actions }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const items = compactActions(actions);
  if (items.length === 0) return null;
  return (
    <>
      <IconButton
        size="small"
        aria-label={t('common.actions', 'Actions')}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={(e) => e.stopPropagation()}>
        {items.map((a) => (
          <MenuItem
            key={a.key}
            disabled={a.disabled}
            {...(a.href ? { component: 'a', href: a.href, target: '_blank', rel: 'noopener noreferrer' } : {})}
            onClick={() => {
              setAnchor(null);
              if (!a.href) a.onClick?.();
            }}
            sx={a.color ? { color: `${a.color}.main` } : undefined}
          >
            <ListItemIcon sx={a.color ? { color: `${a.color}.main` } : undefined}>{a.icon}</ListItemIcon>
            <ListItemText>{a.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export interface StripField {
  label: string;
  value: React.ReactNode;
}

const hasValue = (v: React.ReactNode) => v !== null && v !== undefined && v !== false && v !== '';

/**
 * Strip shown under a selected row: a few key fields, a one-line description
 * and buttons. Fields with no value are skipped.
 */
export const DetailStrip: React.FC<{ description?: React.ReactNode; actions: MaybeActions; fields?: StripField[] }> = ({
  description, actions, fields,
}) => {
  const shown = (fields ?? []).filter((f) => hasValue(f.value));
  return (
    <>
      {shown.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 1, mb: 1.25 }}>
          {shown.map((f) => (
            <DetailItem key={f.label} label={f.label}>{f.value}</DetailItem>
          ))}
        </Box>
      )}
      <DetailStripBar description={description} actions={actions} />
    </>
  );
};

const DetailStripBar: React.FC<{ description?: React.ReactNode; actions: MaybeActions }> = ({ description, actions }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
    <Typography variant="body2" color="text.secondary" component="div" sx={{ flex: '1 1 240px', minWidth: 0 }}>
      {description}
    </Typography>
    {compactActions(actions).map((a) =>
      a.href ? (
        <Button
          key={a.key}
          size="small"
          variant="outlined"
          color={a.color}
          startIcon={a.icon}
          disabled={a.disabled}
          href={a.href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textTransform: 'none' }}
        >
          {a.label}
        </Button>
      ) : (
        <Button
          key={a.key}
          size="small"
          variant="outlined"
          color={a.color}
          startIcon={a.icon}
          disabled={a.disabled}
          onClick={a.onClick}
          sx={{ textTransform: 'none' }}
        >
          {a.label}
        </Button>
      )
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Selectable rows
// ---------------------------------------------------------------------------

/**
 * Selection shared by the list and the map: clicking a row toggles it,
 * clicking a pin selects it and scrolls its row into view.
 */
export function useRowSelection() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const refs = useRef<Record<string, HTMLElement | null>>({});
  const toggle = (id: string) => setSelectedId((prev) => (prev === id ? null : id));
  const selectAndReveal = (id: string) => {
    setSelectedId(id);
    setTimeout(() => refs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };
  const rowRef = (id: string) => (el: HTMLElement | null) => {
    refs.current[id] = el;
  };
  return { selectedId, setSelectedId, toggle, selectAndReveal, rowRef };
}

interface SelectableRowProps {
  selected: boolean;
  onClick: () => void;
  colSpan: number;
  detail: React.ReactNode;
  rowRef?: (el: HTMLElement | null) => void;
  children: React.ReactNode;
}

/** Desktop table row with a green edge when selected and its detail strip underneath. */
export const SelectableTableRow: React.FC<SelectableRowProps> = ({ selected, onClick, colSpan, detail, rowRef, children }) => (
  <>
    <TableRow
      hover
      ref={rowRef}
      selected={selected}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        '& > td': { py: 1.25 },
        '& > td:first-of-type': { borderLeft: '3px solid', borderLeftColor: selected ? 'success.main' : 'transparent' },
      }}
    >
      {children}
    </TableRow>
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: selected ? undefined : 'none' }}>
        <Collapse in={selected} timeout="auto" unmountOnExit>
          <Box sx={{ px: 3, py: 1.5, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
            {detail}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  </>
);

/** Mobile card: header row (name, status, menu), body, and the detail strip when selected. */
export const SelectableCard: React.FC<Omit<SelectableRowProps, 'colSpan' | 'children'> & {
  header: React.ReactNode;
  body?: React.ReactNode;
}> = ({ selected, onClick, detail, rowRef, header, body }) => (
  <Card ref={rowRef} variant="outlined" sx={{ borderRadius: 2, borderColor: selected ? 'success.main' : 'divider' }}>
    <Box onClick={onClick} sx={{ px: 2, py: 1.5, cursor: 'pointer' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>{header}</Box>
      {body && <Box sx={{ mt: 1 }}>{body}</Box>}
    </Box>
    <Collapse in={selected} timeout="auto" unmountOnExit>
      <Box sx={{ px: 2, py: 1.5, backgroundColor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
        {detail}
      </Box>
    </Collapse>
  </Card>
);

// ---------------------------------------------------------------------------
// Map
// ---------------------------------------------------------------------------

const makePinIcon = (fill: string, scale = 1) =>
  new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
        <path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 21.9 12.5 41 12.5 41S25 21.9 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="${fill}" stroke="#FFFFFF" stroke-width="1.5"/>
        <circle cx="12.5" cy="12.5" r="6" fill="white"/>
      </svg>
    `),
    iconSize: [25 * scale, 41 * scale],
    iconAnchor: [12.5 * scale, 41 * scale],
    popupAnchor: [1, -34 * scale],
  });

const DEFAULT_MAP_CENTER: [number, number] = [-25.27, 133.77]; // Australia
const MAP_HEIGHT = { mobile: 180, collapsed: 200, expanded: 440 };

const mapAlive = (map: L.Map) => !!(map as any)._mapPane;

/** Fit the map to the current set of pins whenever they change. */
const FitToPins: React.FC<{ pins: Array<[number, number]> }> = ({ pins }) => {
  const map = useMap();
  useEffect(() => {
    const timeout = setTimeout(() => {
      // Bail if the map was torn down before the delay elapsed
      if (!mapAlive(map)) return;
      map.invalidateSize();
      if (pins.length === 1) {
        map.setView(pins[0], 13);
      } else if (pins.length > 1) {
        // Extra top padding: pins draw upwards from their point, so the
        // northernmost one would otherwise be clipped by the map's top edge
        map.fitBounds(L.latLngBounds(pins), { paddingTopLeft: [40, 60], paddingBottomRight: [40, 30] });
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [map, pins]);
  return null;
};

/** Re-measure the map after its container height or visibility changes. */
const ResizeOnChange: React.FC<{ height: number; open: boolean }> = ({ height, open }) => {
  const map = useMap();
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (mapAlive(map)) map.invalidateSize();
    }, 250);
    return () => clearTimeout(timeout);
  }, [map, height, open]);
  return null;
};

/** Pan to the selected pin. */
const FlyToSelected: React.FC<{ position: [number, number] | null }> = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position && mapAlive(map)) {
      map.flyTo(position, Math.max(map.getZoom(), 13), { duration: 0.6 });
    }
  }, [map, position]);
  return null;
};

export interface MapPin {
  id: string;
  position: [number, number];
  popup: React.ReactNode;
  /** Overrides the map's pinColor (e.g. colour by status). */
  color?: string;
}

interface ListMapProps {
  pins: MapPin[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Records in the current list, for the "x of y on map" label. */
  total?: number;
  /** Records in the current list with no address. */
  missingAddress?: number;
  /** Replaces the "x of y on map" label. */
  label?: React.ReactNode;
  /** Shown over the map when there are no pins. */
  emptyOverlay?: React.ReactNode;
  pinColor: string;
  selectedPinColor: string;
  isMobile: boolean;
  /** Mobile only: the map is opened from the bottom-nav Map button. */
  mobileOpen: boolean;
}

/** Map above the list: pins linked to row selection, coverage label, expand button. */
export const ListMap: React.FC<ListMapProps> = ({
  pins, selectedId, onSelect, total, missingAddress = 0, label, emptyOverlay, pinColor, selectedPinColor, isMobile, mobileOpen,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const icon = useMemo(() => makePinIcon(pinColor), [pinColor]);
  const selectedIcon = useMemo(() => makePinIcon(selectedPinColor, 1.3), [selectedPinColor]);
  // Per-pin colours: one icon pair per distinct colour
  const colorIcons = useMemo(() => {
    const icons = new Map<string, { normal: L.Icon; selected: L.Icon }>();
    pins.forEach((p) => {
      if (p.color && !icons.has(p.color)) {
        icons.set(p.color, { normal: makePinIcon(p.color), selected: makePinIcon(p.color, 1.3) });
      }
    });
    return icons;
  }, [pins]);
  const iconFor = (pin: MapPin, selected: boolean) => {
    const own = pin.color ? colorIcons.get(pin.color) : undefined;
    if (own) return selected ? own.selected : own.normal;
    return selected ? selectedIcon : icon;
  };
  const positions = useMemo(() => pins.map((p) => p.position), [pins]);
  const selectedPosition = useMemo(
    () => pins.find((p) => p.id === selectedId)?.position ?? null,
    [pins, selectedId]
  );
  const open = !isMobile || mobileOpen;
  const height = isMobile ? MAP_HEIGHT.mobile : expanded ? MAP_HEIGHT.expanded : MAP_HEIGHT.collapsed;
  const expandLabel = expanded ? t('listPage.shrinkMap', 'Shrink map') : t('listPage.expandMap', 'Expand map');

  return (
    <Collapse in={open}>
      <Paper
        variant="outlined"
        sx={{ height, width: '100%', mb: 2, overflow: 'hidden', position: 'relative', borderRadius: 2, transition: 'height 0.2s' }}
      >
        <MapContainer
          center={positions[0] || DEFAULT_MAP_CENTER}
          zoom={positions.length ? 10 : 4}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          attributionControl={false}
        >
          <FitToPins pins={positions} />
          <ResizeOnChange height={height} open={open} />
          <FlyToSelected position={selectedPosition} />
          <TileLayer attribution="" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {pins.map((pin) => {
            const selected = pin.id === selectedId;
            return (
              <Marker
                key={pin.id}
                position={pin.position}
                icon={iconFor(pin, selected)}
                zIndexOffset={selected ? 1000 : 0}
                eventHandlers={{ click: () => onSelect(pin.id) }}
              >
                <Popup>{pin.popup}</Popup>
              </Marker>
            );
          })}
        </MapContainer>

        <Box
          sx={{
            position: 'absolute', left: 10, bottom: 10, zIndex: 1000,
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1,
            px: 1, py: 0.25, fontSize: 12, color: 'text.secondary',
          }}
        >
          {label ?? t('listPage.onMapCount', '{{shown}} of {{total}} on map', { shown: pins.length, total: total ?? pins.length })}
          {missingAddress > 0 && (
            <Box component="span" sx={{ color: 'warning.dark' }}>
              {' · '}
              {t('listPage.needAddressCount', '{{count}} need an address', { count: missingAddress })}
            </Box>
          )}
        </Box>
        {pins.length === 0 && emptyOverlay && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              // Clear of the zoom buttons in the top-left corner
              position: 'absolute', top: 10, left: 56, zIndex: 1000,
              bgcolor: 'background.paper', px: 1, py: 0.5, borderRadius: 1,
            }}
          >
            {emptyOverlay}
          </Typography>
        )}
        {!isMobile && (
          <Tooltip title={expandLabel}>
            <IconButton
              size="small"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expandLabel}
              sx={{
                position: 'absolute', right: 10, top: 10, zIndex: 1000,
                bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {expanded ? <CollapseMapIcon fontSize="small" /> : <ExpandMapIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Paper>
    </Collapse>
  );
};
